/**
 * ProjectModal — .pm slide-over with .pm-back backdrop.
 * canonical: S2 lines 1440–1940
 *
 * URL state: ?pm=<projectId> drives open/close.
 * Tabs: Subs & Inbox, Lien, Accounting, Costs & Margin (flagged), Job Cost (flagged).
 * Nested views: SubDetail (#pmSub), DocReview (#pmDoc).
 *
 * No Tailwind — all styles from tokens.css.
 */

import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import type { Project, ProjectTrade, HubInboxItem } from '../../api/types'
import { fetchProjectPayApps } from '../../api/projects'
import { apiFetch } from '../../api/client'
import { sumDraftPayAppAmount } from '../../lib/payAppDrafts.js'
import { isJobcostEnabled, jobcostBaseUrl, type JobcostConfig } from '../../lib/jobcost'
import ModalHeader  from './modal/ModalHeader'
import SubsTab      from './modal/SubsTab'
import LienTab      from './modal/LienTab'
import AccountingTab from './modal/AccountingTab'
import JobCostTab   from './modal/JobCostTab'
import JobcostErrorBoundary from './modal/JobcostErrorBoundary'
import CostsMarginTab from './modal/CostsMarginTab'
import SubDetail    from './modal/SubDetail'
import DocReview    from './modal/DocReview'
import BackToSorenBanner from '../../features/soren/BackToSorenBanner'
import { isCostsMarginTabEnabled, type CostsMarginConfig } from '../../lib/costsMarginFlag'

type Tab   = 'subs' | 'lien' | 'accounting' | 'costs' | 'jobcost'
type Panel = 'tabs' | 'sub-detail' | 'doc-review'

const HUB_TABS: { key: Tab; label: string }[] = [
  { key: 'subs',       label: 'Subs & Inbox' },
  { key: 'lien',       label: 'Lien'         },
  { key: 'accounting', label: 'Accounting'   },
  { key: 'costs',      label: 'Costs & Margin' },
  { key: 'jobcost',    label: 'Job Cost'     },
]

function parseTabParam(
  raw: string | null,
  jobcostOn: boolean,
  costsOn: boolean,
): Tab {
  if (raw === 'payments') return 'accounting'
  if (raw === 'lien' || raw === 'subs' || raw === 'accounting') return raw
  if (raw === 'costs' && costsOn) return 'costs'
  if (raw === 'jobcost' && jobcostOn) return 'jobcost'
  return 'accounting'
}

type InboxFilterParam = 'all' | 'pending' | 'approved' | 'rejected'

function parseInboxFilterParam(raw: string | null): InboxFilterParam | null {
  if (raw === 'pending' || raw === 'approved' || raw === 'rejected' || raw === 'all') return raw
  return null
}

interface Props {
  project:  Project | null
  onClose:  () => void
}

export default function ProjectModal({ project, onClose }: Props) {
  const isOpen = project !== null
  const [searchParams, setSearchParams] = useSearchParams()
  const focus = searchParams.get('focus')

  const { data: cfg } = useQuery({
    queryKey: ['app-config'],
    queryFn: () => apiFetch<JobcostConfig & CostsMarginConfig>('GET', '/api/config'),
    staleTime: 60_000,
  })
  const jobcostOn = isJobcostEnabled(cfg)
  const jobcostUrl = jobcostBaseUrl(cfg)
  const costsOn = isCostsMarginTabEnabled(cfg)

  const { data: payApps = [] } = useQuery({
    queryKey: ['project-payapps', project?.id],
    queryFn: () => fetchProjectPayApps(project!.id),
    enabled: isOpen && project != null,
    staleTime: 30_000,
  })
  const draftTotal = sumDraftPayAppAmount(payApps)

  const [activeTab, setActiveTab] = useState<Tab>('accounting')
  const [panel,     setPanel]     = useState<Panel>('tabs')
  const [activeTrade, setActiveTrade] = useState<ProjectTrade | null>(null)
  const [activeItem,  setActiveItem]  = useState<HubInboxItem | null>(null)
  const [docFromSub, setDocFromSub]   = useState(false)
  const [inboxFilterInit, setInboxFilterInit] = useState<InboxFilterParam | null>(null)

  useEffect(() => {
    if (isOpen) {
      const rawTab = searchParams.get('tab')
      const rawFilter = searchParams.get('filter')
      // Wait for /api/config before applying flagged tabs — clearing ?tab=costs while
      // costsOn is still false leaves the user on Accounting forever.
      if ((rawTab === 'costs' && cfg == null) || (rawTab === 'jobcost' && cfg == null)) {
        return
      }
      setActiveTab(parseTabParam(rawTab, jobcostOn, costsOn))
      setInboxFilterInit(parseInboxFilterParam(rawFilter))
      setPanel('tabs')
      setActiveTrade(null)
      setActiveItem(null)
      if (rawTab != null || rawFilter != null) {
        const next = new URLSearchParams(searchParams)
        next.delete('tab')
        next.delete('filter')
        setSearchParams(next, { replace: true })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, isOpen, jobcostOn, costsOn, cfg])

  useEffect(() => {
    if (!isOpen || !focus) return
    const kind = focus.split(':')[0]
    if (kind === 'vendor_invoice') setActiveTab('subs')
    else if (kind === 'pay_app') setActiveTab('accounting')
    else if (kind === 'assumption' && costsOn) setActiveTab('costs')
  }, [isOpen, focus, project?.id, costsOn])

  useEffect(() => {
    if (!isOpen || !focus) return
    const timer = window.setTimeout(() => {
      const el = document.querySelector(`[data-focus-id="${CSS.escape(focus)}"]`)
      if (el instanceof HTMLElement) {
        el.classList.add('pm-focus-row')
        el.scrollIntoView({ block: 'center' })
      }
    }, 120)
    return () => window.clearTimeout(timer)
  }, [isOpen, focus, activeTab, payApps])

  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isOpen, onClose])

  function openSubDetail(trade: ProjectTrade) {
    setActiveTrade(trade)
    setPanel('sub-detail')
  }

  function openDocReview(item: HubInboxItem, fromSub = false) {
    setActiveItem(item)
    setDocFromSub(fromSub)
    setPanel('doc-review')
  }

  function backToTabs() {
    setPanel('tabs')
    setActiveTrade(null)
    setActiveItem(null)
  }

  function backToSubDetail() {
    setPanel('sub-detail')
    setActiveItem(null)
  }

  const visibleTabs = HUB_TABS.filter((t) => {
    if (t.key === 'jobcost') return jobcostOn
    if (t.key === 'costs') return costsOn
    return true
  })

  return (
    <>
      <div
        className={`pm-back${isOpen ? ' on' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        className={`pm${isOpen ? ' on' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={project?.name ?? 'Project details'}
        data-testid="project-focus"
        data-focus={focus || undefined}
      >
        {project && (
          <>
            <BackToSorenBanner />
            {panel === 'tabs' && (
              <ModalHeader
                project={project}
                payApps={payApps}
                draftTotal={draftTotal}
                onClose={onClose}
                onDeleted={onClose}
                onOpenAr={() => {
                  setActiveTab('accounting')
                  const next = new URLSearchParams(searchParams)
                  next.set('hl', 'outstanding')
                  setSearchParams(next, { replace: true })
                }}
              />
            )}

            {panel === 'tabs' && (
              <div className="pm-tabs">
                {visibleTabs.map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    className={`pmt pmt-${key}${activeTab === key ? ' on' : ''}`}
                    onClick={() => setActiveTab(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}

            <div className="pm-body">
              {panel === 'tabs' && activeTab === 'subs' && (
                <SubsTab
                  projectId={project.id}
                  jobcostOn={jobcostOn}
                  initialInboxFilter={inboxFilterInit ?? undefined}
                  onViewSub={openSubDetail}
                  onViewInboxItem={item => openDocReview(item, false)}
                />
              )}
              {panel === 'tabs' && activeTab === 'lien' && (
                <LienTab projectId={project.id} />
              )}
              {panel === 'tabs' && activeTab === 'accounting' && (
                <AccountingTab
                  projectId={project.id}
                  ownerName={project.owner}
                  ownerEmail={project.owner_email}
                  excludeFromForecast={!!project.exclude_from_forecast}
                />
              )}
              {panel === 'tabs' && costsOn && activeTab === 'costs' && (
                <div data-focus-id={focus?.startsWith('assumption:') ? focus : undefined}>
                  <CostsMarginTab
                    projectId={project.id}
                    onOpenChangeOrders={() => setActiveTab('accounting')}
                  />
                </div>
              )}
              {panel === 'tabs' && jobcostOn && activeTab === 'jobcost' && (
                <JobcostErrorBoundary>
                  <JobCostTab projectId={project.id} baseUrl={jobcostUrl} />
                </JobcostErrorBoundary>
              )}

              {panel === 'sub-detail' && activeTrade && (
                <SubDetail
                  projectId={project.id}
                  trade={activeTrade}
                  jobcostOn={jobcostOn}
                  onBack={backToTabs}
                  onViewInboxItem={item => openDocReview(item, true)}
                />
              )}

              {panel === 'doc-review' && activeItem && (
                <DocReview
                  projectId={project.id}
                  item={activeItem}
                  backLabel={docFromSub ? 'Back to Sub detail' : 'Back to Inbox'}
                  onBack={docFromSub ? backToSubDetail : backToTabs}
                  onReviewComplete={updated => setActiveItem(updated)}
                />
              )}
            </div>
          </>
        )}
      </div>
    </>
  )
}
